import React, { Fragment, useEffect, useState } from 'react'
import { Typography, Button, Grid, Fab, Box, SwipeableDrawer, Hidden, Slide, Chip, useMediaQuery, useTheme, Card, CardContent, Badge, CardActions, IconButton } from '@material-ui/core'
import { Trans } from 'react-i18next'
import NavigateNextOutlinedIcon from '@material-ui/icons/NavigateNextOutlined'
import { ArrowBack, CloseSharp, DeleteOutlineOutlined } from '@material-ui/icons'
import { useSelector, useDispatch } from 'react-redux'
import moment from 'moment'
import { mapByIds, useAuth, useFetch, useSWRs } from '../context/useFetch'
import { JobSection } from './JobSection'
import { useStyles } from '../../pages/index'
import i18n from '../i18n/i18n'
import { useUpdate } from '../reducer'
import { groupFilters, useHomeFilters, Choices } from './Filtering'

const FILTERS = {
  Job: (offer, ids) => ids.includes(offer.job.id),
  ContractType: (offer, ids) => ids.includes(offer.contract_type.id),
  Level: (offer, ids) => ids.includes((offer.level || {}).id),
  Remote: (offer, ids) => ids.includes(offer.remote),
  Wilaya: (offer, ids, included) => ids.includes(included.companies[offer.company.id].departement_code.toString()),
  Technology: (offer, ids) => offer.technologies.find(({ id }) => ids.includes(id)),
  Search: (offer, keywords) => keywords.every((keyword) => offer.label.toLowerCase().includes(keyword.toLowerCase())),
  Company: (offer, ids) => ids.includes(offer.company.id)
}

const applyFilters = (offers, filters, included) =>
  offers.filter((offer) =>
    Object.entries(filters)
      .filter(Boolean)
      .every(([type, ids]) => FILTERS[type](offer, ids, included))
  )

export const applyFilterParentJobId = (offers, parentJobId) => {
  return parentJobId ? offers.filter(({ parent_job: { id } }) => id === parentJobId) : offers
}

const getStoredFilters = () => {
  try {
    return localStorage.getObject('filters-5') || {}
  } catch (e) {
    {
    }
  }
}

export const FetchedMain = ({ url = 'job_offers', initialData = {}, source = 'all' }) =>
  useSWRs(
    ({ fetch }) => [
      fetch('offers', url, { queryParams: { include: 'technologies,job,parent_job,company,contract_type,level' } }),
      fetch('contractTypes', 'contract_types'),
      fetch('levels', 'levels'),
      { filters: { data: getStoredFilters() || {} } }
    ],
    { initialData },
    ({ offers }) => {
      const { jobs } = offers.included

      const classes = useStyles()

      const update = useUpdate()
      const auth = useAuth()

      const search = ''

      const filters = useSelector((state) => state.filters || {})
      const filtersGrouped = groupFilters(filters.all || [])
      const filteredOffers = applyFilters(applyFilterParentJobId(offers.data, filters.parentJobId), filtersGrouped, offers.included)

      useEffect(() => {
        // remove filters if the saved ones returns no offers
        if (filteredOffers.length === 0 && (filters.all || []).length > 0) {
          update(['filters'], {})
        }

        if (source !== 'homepage') {
          return
        }

        if (auth.isCompany()) {
          update(['filters', 'all'], [`Company_${auth.getToken().profile_id}`])
        }
      }, [])

      const multipleParentJobs = useSelector(({ offers: { data } }) =>
        Array.from(new Set(data.map(({ parent_job }) => parent_job?.id))).filter(Boolean).length > 1
      )

      const grouped = multipleParentJobs && !filters.parentJobId ? groupByParentJobId(filteredOffers) : groupByJobId(filteredOffers)

      const selectedCategory = filtersGrouped.Job && filtersGrouped.Job.length === 1 ? filtersGrouped.Job[0] : null

      let actif = selectedCategory ? { [selectedCategory]: grouped[selectedCategory] } : grouped

      actif = Object.entries(actif).sort((pA, pB) => moment(pB[1][0].published_at).diff(moment(pA[1][0].published_at)))

      const goToJob = (id) => () => {
        const job = offers.included.jobs[id]

        if (job.parent_job_id) {
          const newFilters = Array.from(new Set([...(filters.all || []), `Job_${job.id}`]))
          update(['filters', 'all'], newFilters)
        } else {
          update(['filters', 'parentJobId'], parseInt(job.id))
        }

        update(['filters', 'all'], [`Job_${id}`])
        location.href = '#search'
      }

      return (
        <Grid container spacing={1}>
          <Hidden smUp>
            <SwipeableDrawer open={filters.expand} onClose={() => update(['filters', 'expand'], false)} style={{ width: '80vw' }}>
              <Box p={3}>
                <Typography variant="h2">
                  <Trans i18nKey="main.filtering.filter" />
                </Typography>

                <DrawerContent />
              </Box>
            </SwipeableDrawer>
          </Hidden>

          {filters.expand && (
            <Hidden xs>
              <Box mb={3}>
                <Card elevation={1} style={{ backgroundColor: 'inherit' }}>
                  <CardActions style={{ justifyContent: 'end' }}>
                    <IconButton onClick={() => update(['filters', 'expand'], false)}><CloseSharp /></IconButton></CardActions>
                  <CardContent>
                    <Grid container direction="row">
                      <DrawerContent />
                    </Grid>
                  </CardContent>
                </Card>
              </Box>
            </Hidden>
          )}
          {filteredOffers.length > 0 ? (
            <Grid xs={12}>
              {actif.map(([id, currentOffers]) => (
                <Fragment>
                  {selectedCategory && (
                    <Grid item>
                      <Box my={3}>
                        <Fab
                          style={{
                            width: '60px',
                            height: '60px',
                            borderRadius: '30px',
                            color: '#fff'
                          }}
                          variant="contained"
                          color="primary"
                          onClick={() => update(['filters', 'all'], [])}>
                          <ArrowBack />
                        </Fab>
                      </Box>
                    </Grid>
                  )}
                  <JobSection
                    job={offers.included.jobs[id]}
                    offers={currentOffers || []}
                    truncate={Boolean(selectedCategory)}
                  />
                  {(currentOffers !== undefined && currentOffers.length > 6 && !selectedCategory) && (
                    <Box mb={5}>
                      <Typography align="center">
                        <Fab onClick={goToJob(id)} color="primary" variant="contained" size="small">
                          <NavigateNextOutlinedIcon />
                        </Fab>
                        <Button variant="text" color="primary" onClick={goToJob(id)} style={{ fontSize: 17, textTransform: 'inherit' }} size="large">
                          <Trans i18nKey="main.show_all" values={{ offerCount: currentOffers.length }} /> {jobs[id].label}
                        </Button>
                      </Typography>
                    </Box>
                  )}
                </Fragment>
              ))}
            </Grid>
          ) : (
            <Grid item xs={12} sm={12}>
              {offers.data.length === 0 ? (
                <Grid container justifyContent="center">
                  <img src="/empty_slate_noOffers.png" style={{ width: '50%' }} />
                  <Grid item xs={12}>
                    <Typography variant="h2" align="center">
                      {i18n.t('main.no_offer.body1')}
                    </Typography>
                    <Typography variant="h4" align="center">
                      {i18n.t('main.no_offer.body2')}
                    </Typography>
                  </Grid>
                </Grid>
              ) : (
                <Fragment>
                  <Grid container justifyContent="center">
                    <img src="/noSearch.png" className={classes.emptySlate} />
                    {auth.isCompany() && filters.all.filter((filter) => filter === `Company_${auth.getToken().profile_id}`).length !== 0 ? (
                      <Grid item xs={12}>
                        <Typography variant="h1" align="center">
                          <Trans i18nKey="main.filtered_company_search_result" />
                        </Typography>
                      </Grid>
                    ) : (
                      <Grid item xs={12}>
                        <Typography variant="h1" align="center">
                          <Trans i18nKey="main.sorry" />
                        </Typography>
                        <Typography align="center">
                          <Trans i18nKey="main.search_result" /> {search && `"${search}"`}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Fragment>
              )}
            </Grid>
          )}
        </Grid>
      )
    }
  )

const DrawerContent = () => {
  const filters = useHomeFilters()

  return (
    <Fragment>
      <Grid item xs={12} sm={2}>
        <Typography variant="h4">
          1. <Trans i18nKey="main.filtering.by_job" />
        </Typography>

        <Choices choices={filters.jobs} />
      </Grid>

      <Grid item xs={12} sm={2}>
        <Typography variant="h4">
          2.
          <Trans i18nKey="main.filtering.by_contract_type" />
        </Typography>

        <Choices choices={filters.contractTypes} showAll />
      </Grid>

      <Grid item xs={12} sm={2}>
        <Typography variant="h4">
          3. <Trans i18nKey="main.filtering.by_level" />
        </Typography>

        <Choices choices={filters.levels} showAll />
      </Grid>

      <Grid item xs={12} sm={2}>
        <Typography variant="h4">
          4. <Trans i18nKey="main.filtering.by_remote" />
        </Typography>

        <Choices choices={filters.remote} showAll />
      </Grid>

      <Grid item xs={12} sm={2}>
        <Typography variant="h4">
          5. <Trans i18nKey="main.filtering.by_department" />
        </Typography>

        <Choices choices={filters.wilayas} />
      </Grid>

      <Grid item xs={12} sm={2}>
        <Typography variant="h4">
          6. <Trans i18nKey="main.filtering.by_techno" />
        </Typography>

        <Choices choices={filters.technos} />
      </Grid>
    </Fragment>
  )
}

const groupByJobId = (offers) =>
  offers.reduce((total, current) => {
    const jobId = current.job.id
    total[jobId] = total[jobId] || []
    total[jobId].push(current)

    return total
  }, {})

const groupByParentJobId = (offers) =>
  offers.reduce((total, current) => {
    const jobId = current.parent_job.id
    total[jobId] = total[jobId] || []
    total[jobId].push(current)

    return total
  }, {})
